import { notify } from '@ps-int/ps-components/src/notifications/slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select } from 'typed-redux-saga';

import { NOTIFICATIONS_MAP } from '../../../../../shared/features/notifications/ps-notifications';
import { toLoggableError } from '../../../../../shared/helpers/errors';
import { ACTION, FEATURE } from '../../../../../shared/helpers/logger-enums';
import { countMetrika, MetrikaParam } from '../../../../../shared/helpers/metrika';
import { cloudApi } from '../../../../../shared/helpers/transport';
import { getConferenceConfig } from '../../../selectors';
import { AdmitParams, NotificationId } from '../../../types';
import { logger } from '../helpers/logger';
import { setHasNewWaitingRoomPeers } from '../slice';

export function* admitAllWaitingRoomPeersSaga({ payload: admitParams }: PayloadAction<AdmitParams>) {
    try {
        yield* call(countAdmitAllMetrika, admitParams, 'press');
        const conferenceConfig = yield* select(getConferenceConfig);
        if (!conferenceConfig) {
            return;
        }

        yield* call([cloudApi, cloudApi.waitingRoomAdmitAll], conferenceConfig.resolved_uri);

        yield* put(setHasNewWaitingRoomPeers(false));

        yield* call(countAdmitAllMetrika, admitParams, 'success');

        logger.endFeatureDelta({
            feature: FEATURE.WAITING_ROOM,
            action: ACTION.ALLOW_SEVERAL,
            isSuccess: true,
        });
    } catch (error) {
        const loggableError = toLoggableError(error, {
            method: 'admitAllWaitingRoomPeersSaga',
        });

        logger.endFeatureDelta({
            feature: FEATURE.WAITING_ROOM,
            action: ACTION.ALLOW_SEVERAL,
            isSuccess: false,
        });

        yield* call(countAdmitAllMetrika, admitParams, 'fail', loggableError.name, loggableError.message);
        yield* put(notify(NOTIFICATIONS_MAP[NotificationId.WAITING_ROOM_ADMIT_ALL_ERROR]()));
        logger.error(loggableError);
    }
}

const admitAllFromToastMetrika = {
    press: ['waiting_rooms', 'popup_actions', 'people_waiting_come_in_press'],
    success: ['waiting_rooms', 'popup_actions', 'people_waiting_come_in_success'],
    fail: ['waiting_rooms', 'popup_actions', 'people_waiting_come_in_fail'],
};
const admitAllToLimitMetrika = {
    press: ['waiting_rooms', 'waiting_list', 'let_limit_users_come_in_press'],
    success: ['waiting_rooms', 'waiting_list', 'let_limit_users_come_in_success'],
    fail: ['waiting_rooms', 'waiting_list', 'let_limit_users_come_in_fail'],
};
const admitAllMetrika = {
    press: ['waiting_rooms', 'waiting_list', 'let_all_users_come_in_press'],
    success: ['waiting_rooms', 'waiting_list', 'let_all_users_come_in_success'],
    fail: ['waiting_rooms', 'waiting_list', 'let_all_users_come_in_fail'],
};

function* countAdmitAllMetrika(admitParams: AdmitParams, type: 'press' | 'success' | 'fail', ...args: MetrikaParam[]) {
    try {
        const { toLimit, fromToast } = admitParams ?? {};
        const metrika = fromToast ? admitAllFromToastMetrika : toLimit ? admitAllToLimitMetrika : admitAllMetrika;
        const action = metrika[type];
        yield* call(countMetrika, ...action, ...(args ?? []));
    } catch (error) {
        logger.error(
            toLoggableError(error, {
                method: 'countAdmitAllMetrika',
            })
        );
    }
}
